'use client'

import { parseCookies } from 'nookies'
import type { AnyVariables, DocumentInput, RequestPolicy, UseQueryArgs } from 'urql'
import { useQuery } from 'urql'

import configuration from '~/configuration'
import { destroySessionCookiesClient } from '~/core/utilities/catch-api-error'
import { redirectToAccessDenied } from '~/core/utilities/common'

import {
  SESSION_COOKIE_IS_DISABLE_SIDEBAR_ITEM,
  SESSION_EXPIRES_AT_COOKIE_NAME
} from '../constants/cookies'
import { LIST_KEYS_TENANT_BLOCKED, LIST_KEYS_TENANT_EXPIRED } from '../utilities/catch-api-error'
import { setSessionCookieClient } from './save-session-cookie'

function useQueryGraphQL<Data = any, Variables extends AnyVariables = AnyVariables>({
  query,
  variables,
  shouldPause = true,
  requestPolicy = 'network-only',
  user
}: {
  query: DocumentInput<Data, Variables>
  variables?: UseQueryArgs<Variables, Data>['variables']
  shouldPause?: boolean
  requestPolicy?: RequestPolicy | undefined
  user?: any
}) {
  const [result, reexecuteQuery] = useQuery({
    query,
    variables: variables as Variables,
    pause: shouldPause,
    requestPolicy
  })

  const { data, fetching, error } = result

  let statusCode = 200
  let parseErrors
  if (error) {
    const { graphQLErrors, networkError } = error
    const cookies = parseCookies()
    if (networkError) {
      console.error('Network Error')
    } else {
      if (graphQLErrors.length === 1) {
        parseErrors = JSON.parse(JSON.stringify(graphQLErrors[0]))
        statusCode =
          parseErrors?.message && typeof parseErrors?.message === 'string'
            ? parseErrors?.extensions?.http_code
            : parseErrors?.message?.extensions?.http_code
        const blockedErrorCode =
          parseErrors?.extensions?.error_code || parseErrors?.message?.extensions?.error_code

        if (
          blockedErrorCode &&
          LIST_KEYS_TENANT_BLOCKED.includes(blockedErrorCode) &&
          [401, 403].includes(statusCode)
        ) {
          // Kiểm tra xem đã ở trang block-company chưa để tránh redirect nhiều lần
          const pathnameWithoutLocale =
            typeof window !== 'undefined'
              ? window.location.pathname.replace(/\/(en|ja)\/?/, '/')
              : ''
          const isOnBlockCompanyPage = pathnameWithoutLocale?.startsWith(
            configuration.path.tenantBlocked
          )

          if (!isOnBlockCompanyPage) {
            setSessionCookieClient(
              SESSION_COOKIE_IS_DISABLE_SIDEBAR_ITEM,
              JSON.stringify(true).toString(),
              Number(cookies[SESSION_EXPIRES_AT_COOKIE_NAME])
            )
            // Dùng window.location.replace thay vì href để tránh history entry và ngăn các request khác
            window.location.replace(configuration.path.tenantBlocked)
          }
        } else {
          if (
            blockedErrorCode &&
            LIST_KEYS_TENANT_EXPIRED.includes(blockedErrorCode) &&
            [401, 403].includes(statusCode)
          ) {
            if (user) {
              const expiredPage = user?.ownTenant
                ? configuration.path.settings.plans_expired
                : configuration.path.errorExpiredPlan
              window.location.href = expiredPage
            }
          } else {
            const pathnameWithoutLocale = window.location.pathname.replace(/\/(en|ja)\/?/, '/')
            if (statusCode === 401) {
              destroySessionCookiesClient(pathnameWithoutLocale)
              setTimeout(() => {
                if (pathnameWithoutLocale.startsWith('/careerhub/')) {
                  const slug = pathnameWithoutLocale.split('/')?.[2]
                  window.location.href = configuration.path.careerHub.login(slug as string)
                } else {
                  window.location.href = configuration.path.login
                }
              }, 1500)
            }

            if (statusCode === 403) {
              console.error(
                'You do not have permission to access the requested page. Please contact your system administrator for further assistance.'
              )

              if (pathnameWithoutLocale.startsWith('/careerhub/')) {
                const slug = pathnameWithoutLocale.split('/')?.[2]
                window.location.href = configuration.path.careerHub.errorAccessDenied(
                  slug as string
                )
              } else {
                redirectToAccessDenied()
              }
            }

            if (statusCode === 404) {
              if (pathnameWithoutLocale.startsWith('/careerhub/')) {
                const slug = pathnameWithoutLocale.split('/')?.[2]
                window.location.href = configuration.path.careerHub.error404(slug as string)
              } else window.location.href = configuration.path.error404
            }

            // if (statusCode === 406) {
            //   window.location.href = configuration.path.error406
            // }

            if (statusCode === 500) {
              console.error('Network Error')
            }
          }
        }
      }
    }
  }

  return {
    trigger: reexecuteQuery,
    isLoading: fetching,
    data,
    error: {
      parseErrors,
      statusCode
    }
  }
}

export default useQueryGraphQL
